/**
 * Copyright 2024-2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useEffect, useMemo, useReducer } from "react";
import { When } from "react-if";
import { useParams } from "react-router-dom";
import { Button, theme } from "@nordcloud/gnui";
import {
  FutureActionsForResourceSortFields,
  PastActionsForResourceSortFields,
  SortOrder,
} from "~/generated/graphql";
import { Mode, Sidebar } from "~/components";
import { FilterSidebarFooter } from "~/components/SidebarWithMultiSelect/FilterSidebarFooter";
import { FilterWrapper } from "~/components/SidebarWithMultiSelect/FilterWrapper";
import { SelectableListWithSearch } from "~/components/SidebarWithMultiSelect/SelectableListWithSearch";
import { logErrorCodeCheckboxes } from "~/constants";
import {
  useDisclosure,
  useQueryState,
  useGetAvailableEventActionNames,
  useGetAvailablePlanActionNames,
} from "~/hooks";
import {
  getFilterSidebarState,
  getLivePreviewFilterSidebarFooterState,
  hasFilterChanges,
  isNotEmpty,
  isNotNil,
} from "~/tools";
import { resourceDetailsActionsExcludeFilterMap } from "~/tools/filterStateMaps";
import { useGetIncomingActionsForResource } from "../../hooks/useGetIncomingActionsForResource/useGetIncomingActionsForResource";
import { useGetPastActionsForResource } from "../../hooks/useGetPastActionsForResource/useGetPastActionsForResource";
import {
  ResourceDetailsActionsQuery,
  ActionTiming,
} from "../../types/ResourceDetailsActionsQuery";
import { resourceStatusCheckboxes, actionTypeCheckboxes } from "./constants";
import {
  prepareFutureActionsFilter,
  preparePastActionsFilter,
} from "./resourceDetailsActionFilterBuilders";

type State = {
  resourceStatuses: string[];
  excludeResourceStatuses: boolean;
  actionNames: string[];
  excludeActionNames: boolean;
  actionTypes: string[];
  excludeActionTypes: boolean;
  actionLogOutputs: string[];
  excludeActionLogOutputs: boolean;
};
type ResourceDetailsActionsQueryState = ResourceDetailsActionsQuery & {
  page?: string | number;
  limit?: string | number;
};

type FilterSectionProps = {
  name: string;
  isSelected: boolean;
  selectedOptions: string[];
  selectableItems: Array<{ value: string; label: string; subLabel?: string }>;
  isExcludeSelected: boolean;
  onClear: () => void;
  onMultiSelect: (values: string[]) => void;
  onModeChange: (mode: Mode) => void;
};

function FilterSection({
  name,
  isSelected,
  selectedOptions,
  selectableItems,
  isExcludeSelected,
  onClear,
  onMultiSelect,
  onModeChange,
}: FilterSectionProps) {
  return (
    <FilterWrapper
      name={name}
      isSelected={isSelected}
      selectedOptions={selectedOptions}
      onClear={onClear}
    >
      <SelectableListWithSearch
        allowModeChange
        selectedOptions={selectedOptions}
        selectableItems={selectableItems}
        isExcludeSelected={isExcludeSelected}
        onMultiSelect={onMultiSelect}
        onModeChange={onModeChange}
      />
    </FilterWrapper>
  );
}

function buildState(queryState: ResourceDetailsActionsQuery) {
  return {
    resourceStatuses: queryState.resourceStatuses ?? [],
    excludeResourceStatuses: queryState.excludeResourceStatuses ?? false,
    actionNames: queryState.actionNames ?? [],
    excludeActionNames: queryState.excludeActionNames ?? false,
    actionTypes: queryState.actionTypes ?? [],
    excludeActionTypes: queryState.excludeActionTypes ?? false,
    actionLogOutputs: queryState.actionLogOutputs ?? [],
    excludeActionLogOutputs: queryState.excludeActionLogOutputs ?? false,
  };
}

function buildQueryStateFromSidebarState(
  queryState: ResourceDetailsActionsQueryState,
  state: State
): ResourceDetailsActionsQueryState {
  return {
    ...queryState,
    resourceStatuses: state.resourceStatuses,
    excludeResourceStatuses: state.excludeResourceStatuses,
    actionNames: state.actionNames,
    excludeActionNames: state.excludeActionNames,
    actionTypes: state.actionTypes,
    excludeActionTypes: state.excludeActionTypes,
    actionLogOutputs: state.actionLogOutputs,
    excludeActionLogOutputs: state.excludeActionLogOutputs,
  };
}

function getActionNameCheckboxes(
  isPastCurrentActions: boolean,
  availableEventActionNames: string[],
  availablePlanActionNames: string[]
) {
  const actionNames = isPastCurrentActions
    ? availableEventActionNames
    : availablePlanActionNames;

  return actionNames.map((actionName) => ({
    value: actionName,
    label: actionName,
  }));
}

function getPreviewState({
  isPastCurrentActions,
  pastPreviewLoading,
  pastPreviewCount,
  incomingPreviewLoading,
  incomingPreviewCount,
}: {
  isPastCurrentActions: boolean;
  pastPreviewLoading: boolean;
  pastPreviewCount?: number;
  incomingPreviewLoading: boolean;
  incomingPreviewCount?: number;
}) {
  if (isPastCurrentActions) {
    return {
      previewLoading: pastPreviewLoading,
      previewResultsCount: pastPreviewLoading ? undefined : pastPreviewCount,
    };
  }

  return {
    previewLoading: incomingPreviewLoading,
    previewResultsCount: incomingPreviewLoading
      ? undefined
      : incomingPreviewCount,
  };
}

function isSelectionPresent(values: string[]) {
  return isNotNil(values) && isNotEmpty(values);
}

function useActionsPreviewState({
  draftQueryState,
  resourceId,
  isOpen,
  hasPendingChanges,
  isPastCurrentActions,
  phrase,
}: {
  draftQueryState: ResourceDetailsActionsQueryState;
  resourceId: string;
  isOpen: boolean;
  hasPendingChanges: boolean;
  isPastCurrentActions: boolean;
  phrase?: string;
}) {
  const shouldSkipPastPreview =
    !isOpen || !hasPendingChanges || !isPastCurrentActions;
  const shouldSkipIncomingPreview =
    !isOpen || !hasPendingChanges || isPastCurrentActions;

  const { count: pastPreviewCount, loading: pastPreviewLoading } =
    useGetPastActionsForResource({
      resourceId,
      page: String(draftQueryState.page ?? "1"),
      limit: String(draftQueryState.limit ?? "20"),
      sort: {
        field:
          (draftQueryState.field as PastActionsForResourceSortFields) ??
          PastActionsForResourceSortFields.EndTime,
        order: draftQueryState.order ?? SortOrder.Desc,
      },
      filter: preparePastActionsFilter(draftQueryState, phrase),
      skip: shouldSkipPastPreview,
    });

  const { count: incomingPreviewCount, loading: incomingPreviewLoading } =
    useGetIncomingActionsForResource({
      resourceId,
      page: String(draftQueryState.page ?? "1"),
      limit: String(draftQueryState.limit ?? "20"),
      sort: {
        field:
          (draftQueryState.field as FutureActionsForResourceSortFields) ??
          FutureActionsForResourceSortFields.StartTime,
        order: draftQueryState.order ?? SortOrder.Asc,
      },
      filter: prepareFutureActionsFilter(draftQueryState, phrase),
      skip: shouldSkipIncomingPreview,
    });

  return getPreviewState({
    isPastCurrentActions,
    pastPreviewLoading,
    pastPreviewCount,
    incomingPreviewLoading,
    incomingPreviewCount,
  });
}

export function ResourceDetailsActionsFiltersSidebar({
  resultsCount = 0,
}: {
  resultsCount?: number;
}) {
  const { isOpen, open, close } = useDisclosure();
  const { queryState, updateQueryState } =
    useQueryState<ResourceDetailsActionsQuery>();
  const { resourceId } = useParams<{ resourceId: string }>();

  const [state, updateState] = useReducer(
    (data: State, partialData: Partial<State>) => ({ ...data, ...partialData }),
    buildState(queryState)
  );

  useEffect(() => {
    updateState(buildState(queryState));
  }, [queryState, isOpen]);
  const appliedState = buildState(queryState);
  const hasPendingChanges = hasFilterChanges(
    state,
    appliedState,
    resourceDetailsActionsExcludeFilterMap
  );
  const draftQueryState = useMemo(
    () => buildQueryStateFromSidebarState(queryState, state),
    [queryState, state]
  );
  const phrase = isNotEmpty(draftQueryState.searchPhrase?.trim() || "")
    ? draftQueryState.searchPhrase
    : undefined;

  const isPastCurrentActions =
    queryState.actionTiming === ActionTiming.pastCurrent;

  const { data: availableEventActionNames } = useGetAvailableEventActionNames({
    skip: !isPastCurrentActions,
  });
  const { data: availablePlanActionNames } = useGetAvailablePlanActionNames({
    skip: isPastCurrentActions,
  });

  const actionNameCheckboxes = getActionNameCheckboxes(
    isPastCurrentActions,
    availableEventActionNames,
    availablePlanActionNames
  );

  const { previewLoading, previewResultsCount } = useActionsPreviewState({
    draftQueryState,
    resourceId: resourceId ?? "",
    isOpen,
    hasPendingChanges,
    isPastCurrentActions,
    phrase,
  });
  const previewFooterState = getLivePreviewFilterSidebarFooterState({
    hasPendingChanges,
    isOpen,
    previewLoading,
    previewResultsCount,
    appliedResultsCount: resultsCount,
    entityLabel: "actions",
  });

  const emptyState: State = {
    resourceStatuses: [],
    excludeResourceStatuses: false,
    actionNames: [],
    excludeActionNames: false,
    actionTypes: [],
    excludeActionTypes: false,
    actionLogOutputs: [],
    excludeActionLogOutputs: false,
  };

  const clearResourceStatusFilters = () => {
    updateState({ resourceStatuses: [], excludeResourceStatuses: false });
  };

  const clearActionNamesFilter = () => {
    updateState({ actionNames: [], excludeActionNames: false });
  };

  const clearActionTypesFilter = () => {
    updateState({ actionTypes: [], excludeActionTypes: false });
  };

  const clearActionLogOutputsFilter = () => {
    updateState({ actionLogOutputs: [], excludeActionLogOutputs: false });
  };

  const clearAllFilters = () => {
    updateState(emptyState);
    updateQueryState({
      ...queryState,
      ...emptyState,
      page: "1",
    });
    close();
  };

  const applyFilters = () => {
    const nextQueryState = buildQueryStateFromSidebarState(queryState, state);
    updateQueryState({
      ...nextQueryState,
      limit: String(nextQueryState.limit ?? queryState.limit ?? "20"),
      page: "1",
    });
    close();
  };
  const isStatusSelected = isSelectionPresent(state.resourceStatuses);
  const isActionNamesSelected = isSelectionPresent(state.actionNames);
  const isActionTypesSelected = isSelectionPresent(state.actionTypes);
  const isActionLogOutputsSelected = isSelectionPresent(state.actionLogOutputs);

  const handleStatusMultiSelect = (statuses: string[]) => {
    updateState({ resourceStatuses: statuses });
  };

  const handleStatusModeChange = (mode: Mode) => {
    updateState({ excludeResourceStatuses: mode === Mode.EXCLUDES });
  };

  const handleActionNamesMultiSelect = (names: string[]) => {
    updateState({ actionNames: names });
  };

  const handleActionNamesModeChange = (mode: Mode) => {
    updateState({ excludeActionNames: mode === Mode.EXCLUDES });
  };

  const handleActionTypesMultiSelect = (types: string[]) => {
    updateState({ actionTypes: types });
  };

  const handleActionTypesModeChange = (mode: Mode) => {
    updateState({ excludeActionTypes: mode === Mode.EXCLUDES });
  };

  const handleActionLogOutputsMultiSelect = (logOutputs: string[]) => {
    updateState({ actionLogOutputs: logOutputs });
  };

  const handleActionLogOutputsModeChange = (mode: Mode) => {
    updateState({ excludeActionLogOutputs: mode === Mode.EXCLUDES });
  };
  const filterSidebarState = getFilterSidebarState({
    currentState: state,
    appliedState,
    resultsCount: previewFooterState.effectiveResultsCount,
    entityLabel: "actions",
    excludeFilterMap: resourceDetailsActionsExcludeFilterMap,
  });
  const helperText =
    previewFooterState.helperTextOverride ?? filterSidebarState.helperText;

  return (
    <>
      <Button
        mr={theme.spacing.spacing04}
        severity="low"
        icon="filter"
        onClick={open}
      >
        Filters
      </Button>
      <When condition={isOpen}>
        <Sidebar
          title="Filter Results"
          isOpen={isOpen}
          onClick={close}
          footer={
            <FilterSidebarFooter
              helperText={helperText}
              isApplyDisabled={filterSidebarState.isApplyDisabled}
              isClearDisabled={filterSidebarState.isClearDisabled}
              onApply={applyFilters}
              onClear={clearAllFilters}
            />
          }
        >
          <When condition={isPastCurrentActions}>
            <FilterSection
              name="Status"
              isSelected={isStatusSelected}
              selectedOptions={state.resourceStatuses ?? []}
              selectableItems={resourceStatusCheckboxes}
              isExcludeSelected={state.excludeResourceStatuses}
              onClear={clearResourceStatusFilters}
              onMultiSelect={handleStatusMultiSelect}
              onModeChange={handleStatusModeChange}
            />
          </When>
          <FilterSection
            name="Action Name"
            isSelected={isActionNamesSelected}
            selectedOptions={state.actionNames ?? []}
            selectableItems={actionNameCheckboxes}
            isExcludeSelected={state.excludeActionNames}
            onClear={clearActionNamesFilter}
            onMultiSelect={handleActionNamesMultiSelect}
            onModeChange={handleActionNamesModeChange}
          />
          <FilterSection
            name="Action Type"
            isSelected={isActionTypesSelected}
            selectedOptions={state.actionTypes ?? []}
            selectableItems={actionTypeCheckboxes}
            isExcludeSelected={state.excludeActionTypes}
            onClear={clearActionTypesFilter}
            onMultiSelect={handleActionTypesMultiSelect}
            onModeChange={handleActionTypesModeChange}
          />
          <When condition={isPastCurrentActions}>
            <FilterSection
              name="Log Output"
              isSelected={isActionLogOutputsSelected}
              selectedOptions={state.actionLogOutputs ?? []}
              selectableItems={logErrorCodeCheckboxes}
              isExcludeSelected={state.excludeActionLogOutputs}
              onClear={clearActionLogOutputsFilter}
              onMultiSelect={handleActionLogOutputsMultiSelect}
              onModeChange={handleActionLogOutputsModeChange}
            />
          </When>
        </Sidebar>
      </When>
    </>
  );
}
